// Events.jsx
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import Lightbox from "yet-another-react-lightbox";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";

// === HERO IMAGE ===
import eventsHeroImage from '../assets/Japan/capodanno-giapponese-tokyo.webp'; // Update with a good Japan festival image

// === EVENT IMAGES ===
// Replace these with your actual festival images
import event1 from '../assets/Japan/a0004909_parts_60547fd7a2238.webp';
import event2 from '../assets/Japan/images (7).jpeg';
import event3 from '../assets/Japan/images (8).jpeg';
import event4 from '../assets/Japan/AomoriNebutaNeputaFestivals.jpg';
import event5 from '../assets/Japan/takayama-spring-festival-karakuri-marionette.jpg';
import event6 from '../assets/Japan/Untitled+design.webp';
import event7 from '../assets/Japan/winter-fireworks-japan_4.jpg';
import event8 from '../assets/Japan/阿波おどり-1400x720.jpg';
import event9 from '../assets/Japan/hina1.jpg';
import event10 from '../assets/Japan/images (9).jpeg';
import event11 from '../assets/Japan/images (10).jpeg';
// Add more images as needed (up to 20)

const sectionVariants = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.9, ease: "easeOut" } },
};

const Events = () => {
    const { t } = useTranslation('events');
    const [index, setIndex] = useState(-1);
    const location = useLocation();

    const getTabLinkClass = (isActive) =>
        `pb-1 text-sm font-semibold uppercase tracking-[0.25em] transition ${isActive
            ? 'border-b-2 border-[#7fd3ff] text-[#7fd3ff]'
            : 'text-white/70 hover:text-[#f7d774]'}`;

    // === 20 JAPAN FESTIVALS ===
    const events = [
        { image: event1, label: t('sakuraMatsuri'), span: 'col-span-1 row-span-1' },
        { image: event2, label: t('gionMatsuri'), span: 'col-span-1 row-span-1 sm:row-span-2' },
        { image: event3, label: t('sapporoSnow'), span: 'col-span-1 row-span-1' },
        { image: event4, label: t('nebutaMatsuri'), span: 'col-span-1 row-span-1 sm:row-span-2' },
        { image: event5, label: t('takayamaSpring'), span: 'col-span-1 row-span-1 sm:row-span-2' },
        { image: event6, label: t('obonFestival'), span: 'col-span-1 row-span-1 sm:row-span-2' },
        { image: event7, label: t('sumidaFireworks'), span: 'col-span-1 row-span-1' },
        { image: event8, label: t('awaOdori'), span: 'col-span-1 row-span-1' },
        { image: event9, label: t('hinaMatsuri'), span: 'col-span-1 row-span-1' },
        { image: event10, label: t('kandaMatsuri'), span: 'col-span-1 row-span-1' },
        { image: event11, label: t('fujiShibazakura'), span: 'col-span-1 row-span-1' },
        // Continue adding the remaining 10 with appropriate images...
        // Example placeholders:
        // { image: eventX, label: t('aoiMatsuri') },
        // { image: eventX, label: t('tenjinMatsuri') },
        // ... up to 20 total
    ];

    const slides = events.map((item) => ({
        src: item.image,
        width: 1600,
        height: 1067,
        alt: item.label,
        title: item.label,
    }));

    return (
        <div className="min-h-screen bg-[#07111f] text-white">
            {/* HERO SECTION */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="relative overflow-hidden"
            >
                <img
                    src={eventsHeroImage}
                    alt={t('heroTitle')}
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#07111f]/90 via-[#07111f]/75 to-[#07111f]/40" />

                <div className="relative mx-auto flex min-h-[70svh] max-w-7xl items-center px-5 py-16 sm:min-h-[78vh] sm:px-10 sm:py-24 lg:px-16">
                    <div className="max-w-3xl">
                        <p className="mb-4 text-sm font-semibold uppercase tracking-[0.35em] text-[#f7d774]">
                            {t('tabEvents')}
                        </p>
                        <h1 className="font-serif text-[2.2rem] font-semibold leading-tight sm:text-5xl lg:text-6xl">
                            <span className="block text-[#7fd3ff]">{t('heroTitle')}</span>
                            <span className="mt-3 block text-white">{t('tabEvents')}</span>
                        </h1>
                        <p className="mt-5 max-w-2xl text-base leading-7 text-white/90 sm:mt-6 sm:text-lg sm:leading-8 lg:text-xl">
                            {t('heroSubtitle')}
                        </p>
                    </div>
                </div>
            </motion.section>

            {/* EVENTS GRID SECTION */}
            <section className="mx-auto container px-6 py-20 sm:px-10 lg:px-16">
                <div className="mb-10 flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between sm:gap-6">
                    <div className="max-w-3xl">
                        <p className="text-sm font-semibold uppercase tracking-[0.35em] text-[#7fd3ff]">
                            {t('tabEvents')}
                        </p>
                        <h2 className="mt-4 font-serif text-3xl font-semibold text-white sm:text-4xl">
                            {t('heroTitle')}
                        </h2>
                        <p className="mt-4 text-base leading-7 text-white/80 sm:mt-5 sm:text-lg sm:leading-8">
                            {t('heroSubtitle')}
                        </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 rounded-full border border-white/10 bg-[#0f1d33]/80 px-4 py-3 shadow-[0_20px_50px_rgba(0,0,0,0.25)] sm:gap-4">
                        <Link to="/gallery" className={getTabLinkClass(location.pathname === '/gallery')}>
                            {t('tabPlaces')}
                        </Link>
                        <span className="text-white/40">/</span>
                        <Link to="/events" className={getTabLinkClass(location.pathname === '/events')}>
                            {t('tabEvents')}
                        </Link>
                    </div>
                </div>

                <div className="mt-8 grid auto-rows-[220px] grid-cols-1 gap-4 sm:grid-cols-2 sm:auto-rows-[240px] sm:gap-5 md:grid-cols-3 md:auto-rows-[260px] md:gap-5 lg:auto-rows-[320px] lg:gap-7 xl:gap-8">
                    {events.map((item, i) => (
                        <motion.button
                            key={i}
                            whileHover={{ scale: 1.04, y: -8 }}
                            whileTap={{ scale: 0.96 }}
                            type="button"
                            onClick={() => setIndex(i)}
                            className={`group relative overflow-hidden rounded-[1.5rem] border border-white/10 bg-[#0f1d33] shadow-[0_20px_50px_rgba(0,0,0,0.25)] transition-all duration-500 ${item.span || ''}`}
                        >
                            <img
                                src={item.image}
                                alt={item.label}
                                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                                loading="lazy"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#07111f]/90 via-[#07111f]/20 to-transparent opacity-80 transition-opacity duration-500 group-hover:opacity-100" />
                            <div className="absolute bottom-0 left-0 right-0 p-4 text-left sm:p-5 md:p-6">
                                <h3 className="text-sm font-semibold uppercase tracking-[0.25em] text-[#f7d774] drop-shadow-xl sm:text-base">
                                    {item.label}
                                </h3>
                            </div>
                        </motion.button>
                    ))}
                </div>
            </section>

            {/* LIGHTBOX */}
            <Lightbox
                open={index >= 0}
                close={() => setIndex(-1)}
                index={index}
                slides={slides}
                plugins={[Thumbnails, Zoom]}
                thumbnails={{ position: "bottom", width: 110, height: 80, border: 3, borderRadius: 12, padding: 6, gap: 16, imageFit: "cover" }}
                zoom={{ maxZoomPixelRatio: 4 }}
                styles={{
                    container: { backgroundColor: "rgba(7, 17, 31, 0.98)" },
                    thumbnail: { cursor: "pointer", background: "linear-gradient(45deg, #7fd3ff, #f7d774)", borderRadius: "12px", overflow: "hidden", boxShadow: "0 4px 15px rgba(0,0,0,0.4)" },
                }}
            />
        </div>
    );
};

export default Events;